import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { AppShell } from "./components/AppShell";
import { AccountPage } from "./features/account/AccountPage";
import { ArenaPage } from "./features/arena/ArenaPage";
import { AuditPage } from "./features/audit/AuditPage";
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage } from "./features/auth/AuthPages";
import { AuthShell } from "./features/auth/AuthShell";
import { InvitationPage } from "./features/auth/InvitationPage";
import { CasePage } from "./features/cases/CasePage";
import { CasesPage } from "./features/cases/CasesPage";
import { PartRequestPage } from "./features/cases/PartRequestPage";
import { ContactPage, ContactsPage } from "./features/contacts/ContactsPages";
import { InboxPage, type InboxSearch } from "./features/inbox/InboxPage";
import { KnowledgeDocPage, KnowledgePage } from "./features/knowledge/KnowledgePages";
import { SalesPointsPage } from "./features/knowledge/SalesPoints";
import { PrivacyPage, TermsPage } from "./features/legal/LegalPages";
import { ProductPage, ProductsPage } from "./features/products/ProductsPages";
import { QuotePage } from "./features/quotes/QuotePage";
import { QuotesPage } from "./features/quotes/QuotesPage";
import { ReportsPage } from "./features/reports/ReportsPage";
import { CompanyPage } from "./features/settings/CompanyPage";
import { LUMI_TABS, LumiPage, type LumiTab } from "./features/settings/LumiPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { SetupWizard } from "./features/settings/SetupWizard";
import { authClient } from "./lib/auth";
import {
  type InboxFilter,
  prefetchCases,
  prefetchCatalog,
  prefetchCompany,
  prefetchContacts,
  prefetchKnowledge,
  prefetchLumi,
  prefetchQuotes,
  prefetchSettings,
} from "./lib/queries";
import { queryClient } from "./lib/query-client";

const rootRoute = createRootRoute({ component: Outlet });

async function hasSession() {
  const { data } = await authClient.getSession();
  return !!data;
}

/** ?next= keeps where to land after auth (e.g. back to an invitation). */
const nextSearch = (search: Record<string, unknown>): { next?: string } => ({
  next: typeof search.next === "string" ? search.next : undefined,
});

/** Privacy policy and terms (f6-18): public, outside the sign-in card and the panel. */
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/privacidad", component: PrivacyPage });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/terminos", component: TermsPage });

/** Sign-in, sign-up and password screens share one card, so it can animate between them. */
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthShell,
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: nextSearch,
  beforeLoad: async ({ search }) => {
    if (await hasSession()) throw redirect({ to: search.next?.startsWith("/invitacion/") ? search.next : "/bandeja" });
  },
});

const registerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/registro",
  component: RegisterPage,
  validateSearch: nextSearch,
});

const forgotRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/recuperar",
  component: ForgotPasswordPage,
});

/** The email link lands here with ?token= (or ?error= when it expired). */
const resetRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/restablecer",
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
});

/** Public: anyone with the link sees the invitation; joining needs a session. */
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitacion/$token",
  component: InvitationPage,
});

/** Everything below requires a session. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppShell,
  beforeLoad: async () => {
    if (!(await hasSession())) throw redirect({ to: "/login" });
  },
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/bandeja" });
  },
});

const FILTERS: InboxFilter[] = ["all", "mine", "unassigned", "bot", "closed"];

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bandeja",
  component: InboxPage,
  validateSearch: (search: Record<string, unknown>): InboxSearch => ({
    c: typeof search.c === "string" ? search.c : undefined,
    f: FILTERS.includes(search.f as InboxFilter) ? (search.f as InboxFilter) : undefined,
  }),
});

const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contactos",
  loader: () => prefetchContacts(queryClient),
  component: ContactsPage,
});
const contactRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contactos/$contactId",
  component: ContactPage,
});
/** After-sales cases (f4-01) and, on ?tab=repuestos, spare-part requests (f4-02). */
const casesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/postventa",
  validateSearch: (search: Record<string, unknown>): { tab?: "repuestos" } =>
    search.tab === "repuestos" ? { tab: "repuestos" } : {},
  loader: () => prefetchCases(queryClient),
  component: CasesPage,
});
const caseRoute = createRoute({ getParentRoute: () => appRoute, path: "/postventa/$caseId", component: CasePage });
/** Quotes (f4-03). */
const quotesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cotizaciones",
  loader: () => prefetchQuotes(queryClient),
  component: QuotesPage,
});
/** Management reports (f4-04). */
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reportes",
  component: ReportsPage,
});
/** Who changed what and when (f6-14), for owners and admins. */
const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auditoria",
  component: AuditPage,
});
const quoteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cotizaciones/$quoteId",
  component: QuotePage,
});
const partRequestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/postventa/repuestos/$requestId",
  component: PartRequestPage,
});
const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalogo",
  loader: () => prefetchCatalog(queryClient),
  component: ProductsPage,
});
const productRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalogo/$productId",
  /** para: a new part starts fitting this product ("Nuevo repuesto" on its page). */
  validateSearch: (search: Record<string, unknown>): { para?: string } =>
    typeof search.para === "string" ? { para: search.para } : {},
  component: ProductPage,
});
const knowledgeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/conocimiento",
  loader: () => prefetchKnowledge(queryClient),
  component: KnowledgePage,
});
const companyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/empresa",
  /** ?tab=horario opens the hours, ?tab=cotizaciones the quote settings; the profile is the default. */
  validateSearch: (search: Record<string, unknown>): { tab?: "horario" | "cotizaciones" } =>
    search.tab === "horario" || search.tab === "cotizaciones" ? { tab: search.tab } : {},
  loader: () => prefetchCompany(queryClient),
  component: CompanyPage,
});
const salesPointsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/empresa/puntos",
  loader: () => prefetchCompany(queryClient),
  component: SalesPointsPage,
});
const knowledgeDocRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/conocimiento/$docId",
  /** ?tipo= preselects the kind when a suggested topic starts a new doc. */
  validateSearch: (search: Record<string, unknown>): { tipo?: string } =>
    typeof search.tipo === "string" ? { tipo: search.tipo } : {},
  component: KnowledgeDocPage,
});
const lumiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lumi",
  /** ?tab= picks the settings tab; "Cómo habla" is the default. */
  validateSearch: (search: Record<string, unknown>): { tab?: Exclude<LumiTab, "habla"> } =>
    LUMI_TABS.includes(search.tab as LumiTab) && search.tab !== "habla"
      ? { tab: search.tab as Exclude<LumiTab, "habla"> }
      : {},
  loader: () => prefetchLumi(queryClient),
  component: LumiPage,
});
/** Model arena, test environments only; no link in the panel (it moves to Orbis's internal panel, f6-05). */
const arenaRoute = createRoute({ getParentRoute: () => appRoute, path: "/lumi/arena", component: ArenaPage });
/** The welcome tour: full screen, opened by the panel the first time an owner or admin comes in. */
const welcomeRoute = createRoute({ getParentRoute: () => appRoute, path: "/bienvenida", component: SetupWizard });
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ajustes",
  /** ?seccion=whatsapp opens the WhatsApp tab; the team is the default. */
  validateSearch: (search: Record<string, unknown>): { seccion?: "whatsapp" } =>
    search.seccion === "whatsapp" ? { seccion: "whatsapp" } : {},
  loader: () => prefetchSettings(queryClient),
  component: SettingsPage,
});
const accountRoute = createRoute({ getParentRoute: () => appRoute, path: "/cuenta", component: AccountPage });

/** Addresses from before the menu was reorganized (bookmarks, emails, open tabs). */
const moved = (path: string, to: string) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
  });
const oldProductRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/productos/$productId",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/catalogo/$productId", params, replace: true });
  },
});
const movedRoutes = [
  moved("/productos", "/catalogo"),
  oldProductRoute,
  moved("/conocimiento/puntos", "/empresa/puntos"),
  moved("/ajustes/arena", "/lumi/arena"),
  moved("/lumi/configurar", "/bienvenida"),
];

const routeTree = rootRoute.addChildren([
  authRoute.addChildren([loginRoute, registerRoute, forgotRoute, resetRoute]),
  invitationRoute,
  privacyRoute,
  termsRoute,
  appRoute.addChildren([
    indexRoute,
    inboxRoute,
    contactsRoute,
    contactRoute,
    casesRoute,
    caseRoute,
    partRequestRoute,
    quotesRoute,
    quoteRoute,
    reportsRoute,
    auditRoute,
    productsRoute,
    productRoute,
    knowledgeRoute,
    knowledgeDocRoute,
    companyRoute,
    salesPointsRoute,
    lumiRoute,
    arenaRoute,
    welcomeRoute,
    settingsRoute,
    accountRoute,
    ...movedRoutes,
  ]),
]);

/**
 * Short crossfade when the section or one of its tabs changes (and, on phones, when a chat
 * opens or closes). Switching conversations on desktop stays instant: it happens hundreds
 * of times a day.
 */
const pageTransition = {
  types: ({
    pathChanged,
    fromLocation,
    toLocation,
  }: {
    pathChanged: boolean;
    fromLocation?: { search: Record<string, unknown> };
    toLocation: { search: Record<string, unknown> };
  }) => {
    // Hidden tabs never paint, so a transition there would hold the page blank.
    if (document.visibilityState !== "visible") return false;
    // The first page load has nothing to fade from.
    if (!fromLocation) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    if (pathChanged) return ["page"];
    // Tabs inside a section (?tab= in Empresa and Lumi, ?seccion= in Ajustes).
    const from = fromLocation.search;
    const to = toLocation.search;
    if (from.tab !== to.tab || from.seccion !== to.seccion) return ["page"];
    const chatToggled = Boolean(fromLocation?.search.c) !== Boolean(toLocation.search.c);
    return chatToggled && window.matchMedia("(max-width: 760px)").matches ? ["page"] : false;
  },
};

export const router = createRouter({ routeTree, defaultPreload: "intent", defaultViewTransition: pageTransition });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
